import React, { FC } from 'react';
import { useLingui } from '@lingui/react/macro';

import { DEFAULT_LOCALE, normalizeLocale } from '@rover/constants/src/i18n/locale';
import Section from '@rover/kibble/patterns/Section';
import type { CountryConfiguration } from '@rover/rsdk/src/apiClient/latest/model/countryConfiguration';
import type { FrontendConfiguration } from '@rover/rsdk/src/apiClient/latest/model/frontendConfiguration';
import type { SeoLocation } from '@rover/rsdk/src/apiClient/latest/model/seoLocation';
import type { TrainingSearchResponse } from '@rover/rsdk/src/apiClient/latest/model/trainingSearchResponse';
import { camelize } from '@rover/rsdk/src/modules/Network/object-utilities';

import AppStoreCallout from '../../../../shared/AppStoreCallout/AppStoreCallout';
import TrainingHowBrandWorks from '../../../components/TrainingHowBrandWorks';
import RoverProtectCondensed from '../../../ServiceLanding/components/RoverProtectCondensed';
import SeoLocationFooterLinks from '../../components/SeoLocationFooterLinks';
import SeoLocationPageWrapper from '../../components/SeoLocationPageWrapper';
import SeoLocationText from '../../components/SeoLocationText';
import { PageSectionId, useSectionRef } from '../../hooks/useSectionRef';

import DogTrainingFAQ from './components/DogTrainingFAQ';
import TrainingSeoLocationHero from './components/TrainingSeoLocationHero';
import TrainingSeoLocationResultsCarousel from './components/TrainingSeoLocationResultsCarousel';

export type TrainingSeoLocationPageTemplateProps = {
  seoLocation: SeoLocation;
  searchResponse: TrainingSearchResponse;
  countryConfig: CountryConfiguration;
  frontendConfig: {
    settings: Pick<FrontendConfiguration['settings'], 'language'>;
  };
};

const TrainingSeoLocationPageTemplate: FC<TrainingSeoLocationPageTemplateProps> = ({
  seoLocation,
  searchResponse,
  countryConfig,
  frontendConfig,
}) => {
  const { t } = useLingui();
  const faqsRef = useSectionRef(PageSectionId.FAQs);

  const { details, linkedLocations } = seoLocation;
  const {
    breadcrumbData,
    cityName,
    internalServiceType,
    relatedServices,
    name: locationName,
  } = details;
  const locale = normalizeLocale(frontendConfig.settings.language) ?? DEFAULT_LOCALE;
  const { localizedLinkUrls } = camelize(countryConfig.urls, true);
  const sentenceCasedServiceKeyword =
    details.sentenceCasedServiceKeyword || details.serviceTypeName.toLocaleLowerCase(locale);

  return (
    <SeoLocationPageWrapper seoLocation={seoLocation} countryConfig={countryConfig}>
      <Section>
        <TrainingSeoLocationHero details={details} />
      </Section>

      <RoverProtectCondensed countryConfig={countryConfig} isTraining my={['6x', '12x']} />

      {searchResponse.results.length > 0 && (
        <Section paddingLeft="4x" mb={['16x', '12x']}>
          <TrainingSeoLocationResultsCarousel
            searchResponse={searchResponse}
            heading={t`${locationName}, Find top rated ${sentenceCasedServiceKeyword}`}
            providerNounPlural={internalServiceType.providerNounPlural}
            searchFilters={searchResponse.query}
            countryConfig={countryConfig}
            locale={locale}
          />
        </Section>
      )}

      <TrainingHowBrandWorks serviceTypeName={sentenceCasedServiceKeyword} />

      <Section paddingLeft="4x" mb={['16x', '12x']}>
        <DogTrainingFAQ
          locationName={cityName}
          localizedLinkUrls={localizedLinkUrls}
          htmlRef={faqsRef}
        />
      </Section>

      <SeoLocationText my="12x" text={details.text} />

      <Section mt={['8x', '16x']}>
        <AppStoreCallout featureToShow="training" />
      </Section>

      <SeoLocationFooterLinks
        breadcrumbData={breadcrumbData}
        serviceName={sentenceCasedServiceKeyword}
        locationName={cityName}
        linkedLocations={linkedLocations}
        relatedServices={relatedServices}
        internalServiceTypeSlug={internalServiceType.slug}
      />
    </SeoLocationPageWrapper>
  );
};

export default TrainingSeoLocationPageTemplate;
